import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Input,
  Button,
  PageHeader,
  Textarea,
  FormField,
  Card,
  PageLayout,
  useToast,
} from "@dawa/ui";
import Footer from "../components/Footer";
import { apiFetch } from "../lib/api";

type FormValues = { name: string; email: string; message: string };

export default function Support() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>();
  const { addToast, Toasts } = useToast();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async ({ name, email, message }: FormValues) => {
    setSubmitError(null);
    try {
      const res = await apiFetch("/support/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: "support", name, email, message }),
      });
      if (!res.ok) {
        let details = "Impossible d'envoyer la demande.";
        try {
          const body = (await res.json()) as { details?: string };
          if (body?.details) details = body.details;
        } catch {
          // ignore body parse errors
        }
        throw new Error(details);
      }
      addToast("Demande envoyee. Nous revenons vers vous sous 24h ouvrees.", "success");
      reset();
    } catch (err) {
      const details = err instanceof Error ? err.message : "Impossible d'envoyer la demande.";
      setSubmitError(details);
      addToast(details, "error");
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1">
        <PageLayout maxWidth="full" className="space-y-12">
          <PageHeader
            title="Besoin d’aide ?"
            subtitle="Nous sommes là pour vous aider. Contactez-nous ou consultez notre FAQ."
            breadcrumbs={[{ label: "Dashboard", href: "/dashboard" }, { label: "Support" }]}
          />

          <Card variant="elevated" className="p-8">
            <div className="grid gap-8 md:grid-cols-2">
              <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                <FormField label="Nom" htmlFor="name" required error={errors.name?.message as string | undefined}>
                  <Input
                    id="name"
                    placeholder="Votre nom"
                    required
                    hasError={!!errors.name}
                    {...register("name", { required: "Nom requis" })}
                  />
                </FormField>
                <FormField label="Email" htmlFor="email" required error={errors.email?.message as string | undefined}>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Votre email"
                    autoComplete="email"
                    required
                    hasError={!!errors.email}
                    {...register("email", {
                      required: "Email requis",
                      pattern: { value: /.+@.+\..+/, message: "Email invalide" },
                    })}
                  />
                </FormField>
                <FormField label="Message" htmlFor="message" required error={errors.message?.message as string | undefined}>
                  <Textarea
                    id="message"
                    placeholder="Votre message"
                    required
                    rows={5}
                    hasError={!!errors.message}
                    {...register("message", {
                      required: "Message requis",
                      minLength: { value: 5, message: "Message trop court" },
                    })}
                  />
                </FormField>
                {submitError && <p className="text-sm text-danger">{submitError}</p>}
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  Envoyer
                </Button>
              </form>

              <div className="flex flex-col justify-center space-y-4 text-lg">
                <a
                  href="mailto:contact@daware.io"
                  className="flex items-center space-x-3 hover:underline"
                >
                  <span className="text-2xl">📧</span>
                  <span>Email</span>
                </a>
                <a href="/contact" className="flex items-center space-x-3 hover:underline">
                  <span className="text-2xl">💬</span>
                  <span>Chat</span>
                </a>
                <a href="/faq" className="flex items-center space-x-3 hover:underline">
                  <span className="text-2xl">📖</span>
                  <span>Documentation</span>
                </a>
              </div>
            </div>
          </Card>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold">FAQ</h2>
            <div className="space-y-2">
              <details className="rounded-lg bg-surface p-4 shadow">
                <summary className="cursor-pointer font-medium">
                  Comment puis-je vous contacter ?
                </summary>
                <p className="mt-2 text-sm text-muted">
                  Utilisez le formulaire ci-dessus ou envoyez-nous un email à contact@daware.io.
                </p>
              </details>
              <details className="rounded-lg bg-surface p-4 shadow">
                <summary className="cursor-pointer font-medium">
                  Quand puis-je obtenir une réponse ?
                </summary>
                <p className="mt-2 text-sm text-muted">
                  Nous répondons généralement sous 24 heures ouvrées.
                </p>
              </details>
              <details className="rounded-lg bg-surface p-4 shadow">
                <summary className="cursor-pointer font-medium">
                  Où trouver la documentation ?
                </summary>
                <p className="mt-2 text-sm text-muted">
                  Consultez notre documentation pour des guides détaillés.
                </p>
              </details>
            </div>
          </section>
        </PageLayout>
        <Toasts />
      </main>
      <Footer />
    </div>
  );
}
